import { Injectable } from '@angular/core';
import { resizeImage } from 'src/shared/util/resize-image';

@Injectable({
  providedIn: 'root'
})
export class FilePicker {

  async getImg() {
    const file = await this.pickFile('image/*');
    const base64String = file.dataUrl.replace(`data:${file.format};base64,`, '');

    return {...file, base64String};
  }

  async getMultipleImages() {
    const files = await this.pickMultipleFiles('image/*');
    return files.map(file => {
      const base64String = file.dataUrl.replace(`data:${file.format};base64,`, '');
      return {...file, base64String};
    });
  }

  pickFile(fileTypeFilter = '*'): Promise<{ dataUrl: string, format: string, filename: string }> {
    return new Promise<any>((resolve, reject) => {
      let input = document.querySelector(
        '#_file-capture-input'
      ) as HTMLInputElement;

      const cleanup = () => {
        input.parentNode && input.parentNode.removeChild(input);
      };

      if (!input) {
        input = document.createElement('input') as HTMLInputElement;
        input.id = '_file-capture-input';
        input.type = 'file';
        document.body.appendChild(input);
      }

      input.accept = fileTypeFilter;
      (input as any).capture = true;

      input.removeAttribute('capture');

      input.addEventListener('change', (_e: any) => {
        const file = input.files?.[0];
        if (!file) {
          cleanup();
          reject(new Error("No file selected or selection was cancelled"));
          return;
        }
        const format = file.type;

        let reader = new FileReader();

        if (file instanceof Blob) {
          const realFileReader = (reader as any)._realReader;
          if (realFileReader) {
            reader = realFileReader;
          }
        }

        reader.onload = async () => {

          const isImage = format.includes('image');
          const dataUrl = isImage ? await this.resizeImage(reader.result as string, format) : reader.result;

          resolve({
            dataUrl,
            format,
            filename: file.name
          });

          cleanup();
        };
        reader.readAsDataURL(file);
      });

      input.click();

      input.onerror = (err) => {
        reject(err);
        cleanup();
      };

      input.addEventListener('cancel', () => {
        cleanup();
        reject(new Error("File selection was cancelled"));
      });
    });
  }

  pickMultipleFiles(fileTypeFilter = '*'): Promise<{ dataUrl: string, format: string, filename: string }[]> {
    return new Promise<any>((resolve, reject) => {
      let input = document.querySelector('#_file-capture-input') as HTMLInputElement;

      const cleanup = () => {
        input.parentNode && input.parentNode.removeChild(input);
      };

      if (!input) {
        input = document.createElement('input');
        input.id = '_file-capture-input';
        input.type = 'file';
        input.multiple = true;
        document.body.appendChild(input);
      }

      input.accept = fileTypeFilter;
      input.removeAttribute('capture');

      input.addEventListener('change', async (_e: any) => {
        const files = input.files;
        if (!files || files.length === 0) {
          cleanup();
          reject(new Error("No files selected or selection was cancelled"));
          return;
        }

        try {
          const results = await Promise.all(Array.from(files).map((file) => {
            const format = file.type;
            return new Promise((resolveFile) => {
              const reader = new FileReader();
              reader.onload = async () => {
                const isImage = format.includes('image');
                const dataUrl = isImage ? await resizeImage(reader.result as string, format) : reader.result;
                resolveFile({
                  dataUrl,
                  format,
                  filename: encodeURIComponent(file.name)
                });
              };
              reader.readAsDataURL(file);
            });
          }));

          resolve(results);
          cleanup();
        } catch (error) {
          reject(error);
          cleanup();
        }
      });

      input.click();

      input.onerror = (err) => {
        reject(err);
        cleanup();
      };

      input.addEventListener('cancel', () => {
        cleanup();
        reject(new Error("File selection was cancelled"));
      });
    });
  }

  resizeImage(
    dataUrl: string,
    format: string | null = null,
    quality = 0.5
  ): Promise<string> {
    if (typeof dataUrl !== 'string') {
      return dataUrl;
    }
    if (format === 'heic') {
      format = 'jpeg';
    }
    if (dataUrl.includes('heic')) {
      dataUrl = dataUrl.replace('heic', 'jpeg');
    }
    format = format ?? this.extractFormat(dataUrl);
    const img = document.createElement('img') as HTMLImageElement;
    img.src = dataUrl;

    return new Promise((resolve, reject) => {
      img.onload = (_e) => {
        const canvas = document.createElement('canvas');

        const MAX_WIDTH = 1080;
        const MAX_HEIGHT = 1080;
        let width = img.width;
        let height = img.height;

        // Calculate aspect ratio
        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        const resizedDataUrl: string = canvas.toDataURL(`image/${format}`, quality);
        resolve(resizedDataUrl);
      };

      img.onerror = (err) => {
        console.error('error on resize image', err)
        reject(err);
      };
    });
  }

  extractFormat(dataUrl: string): string {
    return dataUrl.substring("data:image/".length, dataUrl.indexOf(";base64"));
  }
}
